// helpers/api/fetchCalendarData.ts
//
// Convenience helpers that wrap your Fastify calendar routes
// (`/timeframes` and `/periods`) behind a typed, paginated interface.
// Each helper:
//
//   • Accepts a ready-to-use `ApiClient` (already holds auth token).
//   • Allows optional filtering / paging parameters.
//   • Normalises the JSON response into `{ spheres, hasNextPage }`.
//   • Throws a custom error with server-supplied details when a call fails.
//
// Usage:
//
//   const { spheres, hasNextPage } = await fetchTimeframesData(apiClient, {
//     organizationId,
//     search: 'FY',
//     limit: 25
//   });
//
//   const periods = await fetchPeriodsData(apiClient, {
//     organizationId,
//     timeframeKey,
//     limit: 50
//   });
//

import { ApiClient } from '@center-technologies/shared-api-clients';
import { Sphere }   from 'types/global';

/*────────────────────────────
 * Virtual period types
 *───────────────────────────*/
export interface VirtualPeriod {
  /** Deterministic timeframe key (matches materialized sphere's timeframeKey) */
  key: string;
  /** Rendered name from label template */
  name: string;
  /** ISO start date */
  startDate: string;
  /** ISO end date */
  endDate: string;
  /** Semantic level: year, quarter, month, week */
  level: string;
  /** Whether a sphere vertex exists for this period */
  materialized: boolean;
  /** Sphere ID if materialized */
  materializedId?: string;
  /** Whether this level has nested timeframe definitions */
  hasChildren: boolean;
}

/*────────────────────────────
 * Shared error type
 *───────────────────────────*/
export class FetchCalendarError extends Error {
  details: string | string[];
  constructor(message: string, details: string | string[]) {
    super(message);
    this.details = details;
    this.name = 'FetchCalendarError';
  }
}

/*────────────────────────────
 * Timeframes
 *───────────────────────────*/
interface FetchTimeframesParams {
  parentKey?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

interface CalendarListResponse {
  status: 'success';
  data: Sphere[];
  nextCursor: string | null;
}

interface FetchListResult {
  spheres: Sphere[];
  nextCursor: string | null;
  hasNextPage: boolean;
}

/**
 * Fetch paginated Timeframe spheres.
 */
export async function fetchTimeframesData(
  apiClient: ApiClient,
  params: FetchTimeframesParams
): Promise<FetchListResult> {
  const {
    parentKey,
    search,
    limit = 50,
    cursor,
  } = params;

  try {
    const res = await apiClient.get<CalendarListResponse>(
      '/calendar/timeframes',
      { params: { parentKey, search, limit, cursor } }
    );

    return {
      spheres:      res.data,
      nextCursor:   res.nextCursor,
      hasNextPage:  Boolean(res.nextCursor),
    };
  } catch (err: any) {
    const msg = err.response?.data?.error ?? err.message ?? 'Unknown error';
    throw new FetchCalendarError(
      'Failed to fetch Timeframes',
      msg
    );
  }
}

/* ------------------------------------------------------------------------- */
/*  Periods                                                                  */
/* ------------------------------------------------------------------------- */
interface FetchPeriodsParams {
  timeframeKey?: string;    // period.fullPath contains this key
  search?: string;          // substring-match on name
  from?:  Date | string;    // inclusive start-date filter (startDate >= from)
  to?:    Date | string;    // inclusive end-date   filter (endDate   <= to)
  limit?: number;           // page size  (default = 50)
  cursor?: string;          // opaque “nextCursor” from previous page
}

/**
 * Fetch one page of Period spheres from the graph-service.
 */
export async function fetchPeriodsData(
  apiClient: ApiClient,
  params: FetchPeriodsParams
): Promise<FetchListResult> {

  const {
    timeframeKey,
    search,
    from,
    to,
    limit  = 50,
    cursor
  } = params;

  /* serialise Date → ISO-8601 string if the caller passed actual Date objects */
  const fromISO = from instanceof Date ? from.toISOString().substring(0, 10) : from;
  const toISO   = to   instanceof Date ? to.toISOString().substring(0, 10)   : to;

  try {
    /* GET /calendar/periods with the (now extended) query-string */
    const res = await apiClient.get<CalendarListResponse>(
      '/calendar/periods',
      {
        params: {
          timeframeKey,
          search,
          from:   fromISO,
          to:     toISO,
          limit,
          cursor
        }
      }
    );

    return {
      spheres:     res.data,
      nextCursor:  res.nextCursor,
      hasNextPage: Boolean(res.nextCursor)
    };

  } catch (err: any) {

    const msg =
      err.response?.data?.error ??
      err.message ??
      'Unknown error fetching Periods';

    throw new FetchCalendarError('Failed to fetch Periods', msg);
  }
}

/* ------------------------------------------------------------------------- */
/*  Virtual periods                                                          */
/* ------------------------------------------------------------------------- */
interface FetchVirtualPeriodsParams {
  rootTimeframeId: string;   // REQUIRED — the root timeframe sphere ID
  level: 'year' | 'quarter' | 'month' | 'week';  // REQUIRED
  parentKey?: string;        // scopes results to children of this key
  from?:  Date | string;     // inclusive start-date filter
  to?:    Date | string;     // inclusive end-date filter
}

interface VirtualPeriodsResponse {
  status: 'success';
  periods: VirtualPeriod[];
  total: number;
}

interface FetchVirtualPeriodsResult {
  periods: VirtualPeriod[];
  total: number;
}

/**
 * Fetch virtual (computed from RRULE) periods for a root timeframe.
 * Returns both materialized and non-materialized entries.
 */
export async function fetchVirtualPeriods(
  apiClient: ApiClient,
  params: FetchVirtualPeriodsParams
): Promise<FetchVirtualPeriodsResult> {

  const { rootTimeframeId, level, parentKey, from, to } = params;

  if (!rootTimeframeId?.trim()) {
    throw new FetchCalendarError('Invalid parameters', 'rootTimeframeId is required');
  }

  const fromISO = from instanceof Date ? from.toISOString().substring(0, 10) : from;
  const toISO   = to   instanceof Date ? to.toISOString().substring(0, 10)   : to;

  try {
    const res = await apiClient.get<VirtualPeriodsResponse>(
      `/calendar/timeframes/${encodeURIComponent(rootTimeframeId)}/virtual-periods`,
      { params: { level, parentKey, from: fromISO, to: toISO } }
    );

    return {
      periods: res.periods,
      total:   res.total,
    };
  } catch (err: any) {
    const msg = err.response?.data?.error ?? err.message ?? 'Unknown error';
    throw new FetchCalendarError('Failed to fetch virtual periods', msg);
  }
}

/* ------------------------------------------------------------------------- */
/*  Nearby timeframes                                                        */
/* ------------------------------------------------------------------------- */

/** A timeframe discovered via proximity to a sphere's center */
export interface NearbyTimeframe {
  /** Vertex ID */
  id: string;
  /** Display name */
  name: string;
  /** Semantic level key (e.g. 'year') */
  timeframeTypeKey: string;
  /** ISO start date */
  startDate: string;
  /** ISO end date */
  endDate: string;
  /** Nested child definitions (for hierarchical timeframes) */
  nestedTimeframes: any[];
  /** True if this is an org-level root timeframe */
  isOrganizationLevel: boolean;
  /** RRULE schedule definition */
  scheduleDefinition: any;
}

interface FetchNearbyTimeframesParams {
  sphereId: string;       // REQUIRED — sphere to find nearby timeframes for
  hops?: number;          // max graph hops from center (default: 3)
}

interface NearbyTimeframesResponse {
  status: 'success';
  timeframes: NearbyTimeframe[];
}

export interface FetchNearbyTimeframesResult {
  timeframes: NearbyTimeframe[];
}

/**
 * Fetch calendar timeframes near a given sphere by walking
 * the graph from the sphere's center. Includes org-level root timeframes.
 */
export async function fetchNearbyTimeframes(
  apiClient: ApiClient,
  params: FetchNearbyTimeframesParams
): Promise<FetchNearbyTimeframesResult> {

  const { sphereId, hops } = params;

  if (!sphereId?.trim()) {
    throw new FetchCalendarError('Invalid parameters', 'sphereId is required');
  }

  try {
    const res = await apiClient.get<NearbyTimeframesResponse>(
      '/calendar/timeframes/nearby',
      { params: { sphereId, hops } }
    );

    return {
      timeframes: res.timeframes,
    };
  } catch (err: any) {
    const msg = err.response?.data?.error ?? err.message ?? 'Unknown error';
    throw new FetchCalendarError('Failed to fetch nearby timeframes', msg);
  }
}

/* ------------------------------------------------------------------------- */
/*  On-demand materialization                                                */
/* ------------------------------------------------------------------------- */
/** The route takes the root timeframe's own organization; the body never names one. */
interface MaterializePeriodParams {
  rootTimeframeId: string;   // REQUIRED
  targetDate: string;        // REQUIRED — ISO date string (e.g. '2026-01-01')
  targetLevel: 'year' | 'quarter' | 'month' | 'week';  // REQUIRED
}

interface MaterializeResponse {
  status: 'success';
  periodSphereId: string;
  createdCount: number;
  skippedCount: number;
}

export interface MaterializePeriodResult {
  periodSphereId: string;
  createdCount: number;
  skippedCount: number;
}

/**
 * Trigger on-demand materialization of a single period and its parent chain.
 * Idempotent — re-calling for an already-materialized period returns 0 createdCount.
 */
export async function materializePeriod(
  apiClient: ApiClient,
  params: MaterializePeriodParams
): Promise<MaterializePeriodResult> {

  const { rootTimeframeId, targetDate, targetLevel } = params;

  if (!rootTimeframeId?.trim()) {
    throw new FetchCalendarError('Invalid parameters', 'rootTimeframeId is required');
  }

  try {
    const res = await apiClient.post<MaterializeResponse>(
      `/calendar/timeframes/${encodeURIComponent(rootTimeframeId)}/materialize`,
      { targetDate, targetLevel }
    );

    return {
      periodSphereId: res.periodSphereId,
      createdCount: res.createdCount,
      skippedCount: res.skippedCount,
    };
  } catch (err: any) {
    const msg = err.response?.data?.error ?? err.message ?? 'Unknown error';
    throw new FetchCalendarError('Failed to materialize period', msg);
  }
}